// The approval, grant and business-approval client over the apps transport.
// It maps the server's typed record into the UI's types and refuses anything
// malformed; it never invents a state. A failed decide call is thrown, not
// guessed: the card then reads as an unknown outcome and offers no repeat.
import { useState } from 'react';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import {
  appsTransport,
  appsTransportBoundTo,
  AppsRouteUnregistered,
  type AppsTransport,
} from './apps-transport';
import type {
  AppApproval,
  AppDecision,
  AppDecisionKind,
  AppGrant,
  ApprovalRow,
  BusinessApproval,
  BusinessStep,
  DecideInput,
  DecideResult,
  DecisionReceiptRequest,
  DecisionReceiptStatus,
  DecisionBlock,
  DecisionOrigin,
  DecisionState,
  GrantAudience,
  RowChange,
} from './app-decision-types';

type Obj = Record<string, unknown>;
const obj = (value: unknown): Obj | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Obj) : null;
const text = (value: unknown): string | null => (typeof value === 'string' ? value : null);
const STATES: readonly DecisionState[] = [
  'pending',
  'approved',
  'rejected',
  'expired',
  'stale',
  'running',
  'applied',
];
const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | null =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : null;

const BLOCK_REASONS = ['approval', 'budget', 'provider', 'viewer_context_required'] as const;

/**
 * The C4 projection, complete or refused: the outcome must be stated, and a
 * blocked reason is either absent/null or one the contract names. Anything
 * else makes the whole record unreadable, so no card can offer a decision on
 * a record whose block it could not read.
 */
function block(raw: Obj): DecisionBlock | null {
  const outcome = oneOf(raw.outcome, ['pending', 'confirmed', 'unknown'] as const);
  if (!outcome) return null;
  if (raw.blockedReason === undefined || raw.blockedReason === null)
    return { blockedReason: null, outcome };
  const blockedReason = oneOf(raw.blockedReason, BLOCK_REASONS);
  return blockedReason ? { blockedReason, outcome } : null;
}

function change(value: unknown): RowChange | null {
  const raw = obj(value);
  const field = raw && text(raw.field);
  const label = raw && text(raw.label);
  if (!raw || !field || label === null) return null;
  // A display value is explicitly a string or null (an absent field): nothing
  // missing or of another type is turned into an empty value.
  const shown = (value: unknown) => typeof value === 'string' || value === null;
  if (!shown(raw.before) || !shown(raw.after)) return null;
  return { field, label, before: raw.before as string | null, after: raw.after as string | null };
}
function row(value: unknown): ApprovalRow | null {
  const raw = obj(value);
  const rowId = raw && text(raw.rowId);
  const action = raw && oneOf(raw.action, ['create', 'update', 'delete'] as const);
  if (!raw || !rowId || !action) return null;
  if (!Array.isArray(raw.changes)) return null;
  const changes = raw.changes.map(change);
  if (changes.includes(null)) return null;
  return { rowId, title: text(raw.title) ?? '', action, changes: changes as RowChange[] };
}
function audience(value: unknown): GrantAudience | null {
  const raw = obj(value);
  if (raw?.kind === 'owner_only') return { kind: 'owner_only' };
  if (raw?.kind === 'company') return { kind: 'company' };
  if (raw?.kind === 'people' && Number.isInteger(raw.count) && (raw.count as number) >= 0)
    return { kind: 'people', count: raw.count as number };
  return null;
}
function step(value: unknown): BusinessStep | null {
  const raw = obj(value);
  const state = raw && oneOf(raw.state, ['waiting', 'pending', 'approved', 'rejected'] as const);
  const assigneeName = raw && text(raw.assigneeName);
  if (!raw || !state || !assigneeName || !Number.isInteger(raw.order)) return null;
  return {
    order: raw.order as number,
    assigneeName,
    state,
    decidedAt: text(raw.decidedAt),
    isYou: raw.isYou === true,
  };
}

function origin(value: unknown): DecisionOrigin | null {
  const raw = obj(value);
  const kind = raw && oneOf(raw.kind, ['app', 'agent', 'client'] as const);
  return kind ? { kind, label: text(raw.label) } : null;
}

export function parseDecision(kind: AppDecisionKind, value: unknown): AppDecision | null {
  const raw = obj(value);
  const id = raw && text(raw.id);
  const version = raw && text(raw.version);
  const state = raw && oneOf(raw.state, STATES);
  const stated = raw && block(raw);
  if (!raw || !id || !version || !state || !stated) return null;
  const common = {
    id,
    version,
    state,
    requesterName: text(raw.requesterName),
    origin: origin(raw.origin),
    createdAt: text(raw.createdAt) ?? '',
    canDecide: raw.canDecide === true,
    ...stated,
  };
  if (kind === 'approval') {
    if (!Array.isArray(raw.rows)) return null;
    const rows = raw.rows.map(row);
    if (rows.includes(null)) return null;
    const applied = raw.appliedRowCount;
    const record: AppApproval = {
      ...common,
      kind,
      appTitle: text(raw.appTitle) ?? '',
      targetLabel: text(raw.targetLabel) ?? '',
      rows: rows as ApprovalRow[],
      excludedRowIds: (Array.isArray(raw.excludedRowIds) ? raw.excludedRowIds : []).filter(
        (each): each is string => typeof each === 'string',
      ),
      appliedRowCount:
        Number.isInteger(applied) && (applied as number) >= 0 ? (applied as number) : null,
    };
    return record;
  }
  if (kind === 'grant') {
    const access = oneOf(raw.access, ['read', 'write'] as const);
    const who = audience(raw.audience);
    if (!access || !who) return null;
    const record: AppGrant = {
      ...common,
      kind,
      appTitle: text(raw.appTitle) ?? '',
      sourceLabel: text(raw.sourceLabel) ?? '',
      access,
      audience: who,
      ownerName: text(raw.ownerName),
    };
    return record;
  }
  const steps = (Array.isArray(raw.steps) ? raw.steps : []).map(step);
  if (steps.includes(null) || steps.length === 0) return null;
  const record: BusinessApproval = {
    ...common,
    kind,
    title: text(raw.title) ?? '',
    detail: text(raw.detail),
    steps: (steps as BusinessStep[]).sort((a, b) => a.order - b.order),
  };
  return record;
}

export interface AppDecisionsApi {
  get(kind: AppDecisionKind, id: string): Promise<AppDecision>;
  decide(input: DecideInput): Promise<DecideResult>;
  /**
   * The canonical digest of this decision command, from the registered command
   * mapping. Throws when none is registered or it returns nothing usable: the
   * caller then sends nothing.
   */
  commandDigest(command: Omit<DecideInput, 'inputDigest'>): Promise<string>;
  /** Read-only receipt lookup for a lost decision; never sends the decision. */
  receiptStatus(request: DecisionReceiptRequest): Promise<DecisionReceiptStatus>;
}

const name = (kind: AppDecisionKind, action: 'get' | 'decide' | 'receipt') =>
  `${kind === 'approval' ? 'approval' : kind === 'grant' ? 'grant' : 'business'}.${action}` as const;

const ECHOED = ['kind', 'id', 'expectedVersion', 'decision', 'operationId', 'inputDigest'] as const;

/**
 * Fail closed: anything but an exactly shaped answer that echoes all six
 * fields of the original request is `unknown`.
 */
export function parseReceiptStatus(
  value: unknown,
  request: DecisionReceiptRequest,
): DecisionReceiptStatus {
  const unknown: DecisionReceiptStatus = { status: 'unknown' };
  const raw = obj(value);
  if (!raw) return unknown;
  for (const field of ECHOED) if (raw[field] !== request[field]) return unknown;
  const status = oneOf(raw.status, ['applied', 'pending', 'not_applied', 'unknown'] as const);
  if (!status) return unknown;
  if (status === 'pending' || status === 'unknown') return { status };
  if (status === 'not_applied') {
    const terminalRef = text(raw.terminalRef);
    return terminalRef ? { status, terminalRef } : unknown;
  }
  const receiptId = text(raw.receiptId);
  const record = receiptId ? parseDecision(request.kind, raw.record) : null;
  if (!receiptId || !record || record.kind !== request.kind || record.id !== request.id)
    return unknown;
  return { status: 'applied', receiptId, record };
}

export function createAppDecisionsApi(transport: AppsTransport = appsTransport): AppDecisionsApi {
  return {
    async get(kind, id) {
      const response = await transport.request(name(kind, 'get'), id);
      const record = response.status === 200 ? parseDecision(kind, response.body) : null;
      if (!record) throw new Error('load_failed');
      return record;
    },
    async commandDigest(command) {
      if (!transport.commandDigest) throw new AppsRouteUnregistered('commandDigest');
      const digest = await transport.commandDigest({ ...command });
      if (typeof digest !== 'string' || digest.length === 0) throw new Error('digest_unusable');
      return digest;
    },
    async receiptStatus(request) {
      try {
        const response = await transport.request(name(request.kind, 'receipt'), request.id, {
          kind: request.kind,
          id: request.id,
          expectedVersion: request.expectedVersion,
          decision: request.decision,
          operationId: request.operationId,
          inputDigest: request.inputDigest,
        });
        return response.status === 200
          ? parseReceiptStatus(response.body, request)
          : { status: 'unknown' };
      } catch {
        return { status: 'unknown' };
      }
    },
    async decide(input) {
      const response = await transport.request(name(input.kind, 'decide'), input.id, {
        expectedVersion: input.expectedVersion,
        decision: input.decision,
        excludedRowIds: input.excludedRowIds,
        stepOrder: input.stepOrder,
        operationId: input.operationId,
        inputDigest: input.inputDigest,
      });
      if (response.status === 200) {
        const record = parseDecision(input.kind, obj(response.body)?.record ?? response.body);
        if (record) return { kind: 'ok', record };
        throw new Error('decide_unreadable');
      }
      if (response.status === 409) {
        const current = parseDecision(input.kind, obj(response.body)?.current);
        if (current) return { kind: 'conflict', current };
        throw new Error('decide_unreadable');
      }
      if (response.status === 403) return { kind: 'refused', reason: 'not_authorized' };
      if (response.status === 410) return { kind: 'refused', reason: 'expired' };
      if (response.status === 400 || response.status === 422)
        return { kind: 'refused', reason: 'invalid' };
      throw new Error('decide_failed');
    },
  };
}

/**
 * The api one mounted decision card uses: an explicit one, else one bound to the
 * session-bound fetch of the identity the card mounted under (never a later guard),
 * so digest, decide and receipt are one action's calls.
 */
export function useDecisionsApi(explicit?: AppDecisionsApi): AppDecisionsApi {
  const bound = useSessionBoundFetch();
  const [own] = useState(() => createAppDecisionsApi(appsTransportBoundTo(bound)));
  return explicit ?? own;
}
